import { useMemo } from 'react';
import { getText } from 'i18n/intl';
import { fieldMappingSelector, fieldsLoading } from 'slices/field.slice';
import {
  selectObjectTypesLoading,
  selectObjectTypesMap,
} from 'slices/objectTypes.slice';
import { useAppSelector } from 'slices/store';
import type { ObjectMapping } from '@timeedit/calendar-integration-api-contract';
import { Form, Space } from '@timeedit/ui-components/antd';
import { Typography } from '@timeedit/ui-components/components/Typography/Typography';
import { MappingSelect } from '../MappingSelect/MappingSelect';

type ObjectMappingSectionProps = {
  title: string;
  name: string;
};

export const ObjectMappingSection = ({
  title,
  name,
}: ObjectMappingSectionProps) => {
  const isLoadingObjectTypes = useAppSelector(selectObjectTypesLoading);
  const isLoadingFields = useAppSelector(fieldsLoading);

  const objectTypesMap = useAppSelector(selectObjectTypesMap);
  const fieldsMap = useAppSelector(fieldMappingSelector);

  const form = Form.useFormInstance<ObjectMapping>();
  const selectedObjectTypeId = Form.useWatch([name, 'objectTypeId'], form);

  const selectedObjectType = useMemo(
    () => objectTypesMap[selectedObjectTypeId],
    [objectTypesMap, selectedObjectTypeId]
  );

  const objectTypeOptions = useMemo(
    () =>
      Object.values(objectTypesMap).map((type) => ({
        label: type.name,
        value: type.id,
      })),
    [objectTypesMap]
  );

  const typeFieldOptions = useMemo(() => {
    if (!selectedObjectType) return [];

    const fieldIds = [
      ...(selectedObjectType.parentFields ?? []),
      ...selectedObjectType.fields,
    ];

    return fieldIds
      .map((fieldId) => {
        const field = fieldsMap[fieldId];
        if (!field) return null;
        return {
          label: field.name,
          value: field.id,
          extId: field.extId,
        };
      })
      .filter(Boolean);
  }, [fieldsMap, selectedObjectType]);

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Typography.Title level={5}>{title}</Typography.Title>
      <MappingSelect
        label={getText('integration.calendar.mapping.object.object_type')}
        name={[name, 'objectTypeId']}
        options={objectTypeOptions}
        loading={isLoadingObjectTypes}
        tooltip={getText(
          `integration.calendar.mapping.object.${name}.object_type_tooltip`
        )}
      />
      <MappingSelect
        label={getText('integration.calendar.mapping.object.object_filter')}
        name={[name, 'objectFieldId']}
        options={typeFieldOptions}
        disabled={!selectedObjectType}
        loading={isLoadingFields}
        tooltip={getText(
          `integration.calendar.mapping.object.object_filter_tooltip`
        )}
      />
      {name === 'room' && (
        <>
          <MappingSelect
            label={getText('integration.calendar.mapping.object.name_field')}
            name={[name, 'nameFieldId']}
            options={typeFieldOptions}
            disabled={!selectedObjectType}
            loading={isLoadingFields}
            tooltip={getText(
              `integration.calendar.mapping.object.room.object_name_field_tooltip`
            )}
          />
        </>
      )}
    </Space>
  );
};
